import {
  CurrencyPicker,
  RegularSelectInput,
  Show,
  SwitchInput,
  TextArea,
  TextInput,
} from "components/core";
import { FC, useEffect } from "react";

import { FormikProps } from "formik";
import clsx from "clsx";
import numeral from "numeral";
import { Offer_findSingleOffer } from "_graphql/visal/queries/__generated__/Offer";

export interface PaymentFormProps {
  form: FormikProps<any>;
  offer?: Offer_findSingleOffer;
  amountToPay: number;
}

const PaymentForm: FC<PaymentFormProps> = ({ form, offer }) => {
  useEffect(() => {
    if (!form.values.payment_details.conversion.addExchangeRate) {
      form.setFieldValue("rate", 1);
      form.setFieldValue(
        "payment_details.currency",
        offer?.offer_detail?.currency
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.payment_details.conversion.addExchangeRate]);

  const isFleet = [
    "Motor Comprehensive Fleet",
    "Motor Comprehensive",
    "Motor Comprehensive (Automobile Fac Facility)",
  ].includes(offer?.classofbusiness?.business_name || "");

  return (
    <div className="space-y-6 divide-y divide-gray-200">
      <div className="border border-gray-300 border-dashed bg-gray-100 divide-y divide-dashed divide-gray-300 rounded-md">
        <div className="grid grid-cols-4 gap-4 p-4">
          <div className="col-span-2">
            <span className="block text-sm font-light text-gray-700">
              Policy Number
            </span>
            <div className="mt-1 block w-full sm:text-sm font-medium">
              {offer?.offer_detail?.policy_number || "N/A"}
            </div>
          </div>
          <div className="col-span-2">
            <span className="block text-sm font-light text-gray-700">
              Insurer
            </span>
            <div className="mt-1 block w-full sm:text-sm font-medium">
              {offer?.insurer?.insurer_company_name || "N/A"}
            </div>
          </div>
          <div className="col-span-2">
            <span className="block text-sm font-light text-gray-700">
              Class of Business
            </span>
            <div className="mt-1 w-full block sm:text-sm font-medium">
              <span>{offer?.classofbusiness?.business_name || "N/A"}</span>
              {isFleet && (
                <span className="font-light">
                  - (
                  {
                    JSON.parse(
                      offer?.offer_detail?.offer_details || "[]"
                    )?.find(
                      (el: { [x: string]: string }) =>
                        el["keydetail"] === "Vehicle Reg No" ||
                        el["keydetail"] === "Vehicle Reg No."
                    )?.value
                  }
                  )
                </span>
              )}
            </div>
          </div>
          <div className="col-span-2">
            <span className="block text-sm font-light text-gray-700">
              Insured
            </span>
            <div className="mt-1 block w-full sm:text-sm font-medium">
              {offer?.offer_detail?.insured_by || "N/A"}
            </div>
          </div>
          <div className="">
            <span className="block text-sm font-light text-gray-700">
              Facultative Offer <b>(%)</b>
            </span>
            <div className="mt-1 block w-full sm:text-sm font-medium">
              {offer?.facultative_offer || "N/A"}
            </div>
          </div>
          <div className="">
            <span className="block text-sm font-light text-gray-700">
              Expected Amount
            </span>
            <div className="mt-1 block w-full sm:text-sm text-green-600 font-medium">
              {form.values.payment_details.conversion.addExchangeRate
                ? form.values.payment_details.conversion.currency
                : form.values.payment_details.currency}{" "}
              {numeral(form.values.payment_details.exptectedAmount).format(
                "#,#.####"
              ) || "N/A"}
            </div>
          </div>
        </div>
        <div className="col-span-2">
          <SwitchInput
            id="payment_details.conversion.addExchangeRate"
            {...form}
            label=" Pay in another currency"
            description="Supply your own exchange rate and currency"
          />
        </div>
      </div>
      <Show if={isFleet}>
        <div className="p-3">
          <label
            htmlFor="selected_vehicles"
            className="block text-sm font-medium text-gray-700"
          >
            Payment will affect the following selected vehicles
          </label>

          <div className="mt-2 grid grid-cols-3 gap-3 sm:grid-cols-5">
            {form.values?.vehiclesToSelect?.map((option: any, idx: number) => (
              <label
                key={idx}
                aria-label={option?.vehicleNumber || ""}
                className="group relative flex items-center justify-center rounded-md border border-gray-300 bg-white p-3
        has-checked:border-indigo-600 has-checked:bg-indigo-600"
              >
                <input
                  type="checkbox"
                  name="selectedVehicles"
                  value={option?.offerId || ""}
                  checked={form.values?.selectedVehicles?.includes(
                    option?.offerId
                  )}
                  onChange={form.handleChange}
                  readOnly={option?.readOnly}
                  disabled={option?.readOnly}
                  className="absolute inset-0 appearance-none"
                />

                <span className="text-sm font-medium text-gray-900 uppercase group-has-checked:text-white">
                  {option?.vehicleNumber}
                </span>
              </label>
            ))}
          </div>
        </div>
      </Show>

      {form.values.payment_details.conversion.addExchangeRate && (
        <div className="bg-yellow-100 p-2 rounded-lg">
          <p className="text-yellow-600 text-xs space-y-2">
            <span>
              {" "}
              ⁠Please supply the currency rate in the following order. If
              conversion is from a Higher currency (e.g. USD, EUR, GBP, or
              similar) to a lower currency, compute as follows
            </span>
            <ul className="list-inside list-disc">
              <li>⁠From GHS to USD if ( 14.90 GHS to 1 USD)</li>
              <li> ⁠⁠Then 1 / 14.90 = 0.067114093959732</li>
            </ul>
            <span>
              {" "}
              This only applies when conversion is from a higher currency to a
              lower currency.
            </span>
          </p>
        </div>
      )}
      {form.values.payment_details.conversion.addExchangeRate && (
        <div className="pt-3">
          <span className="text-xs font-light">Conversion Details</span>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <TextInput
                type="number"
                step={0.01}
                id="payment_details.conversion.rate"
                label="Amount"
                placeholder="e.g. 1200"
                required={true}
                {...form}
              />
            </div>
            <div>
              <CurrencyPicker
                id="payment_details.conversion.currency"
                label="Currency"
                placeholder="e.g. USD"
                required={true}
                {...form}
              />
            </div>
          </div>
        </div>
      )}
      <div className="pt-3">
        {/* <span className="text-xs font-light">Re</span> */}
        <div className={clsx("grid grid-cols-2 gap-2")}>
          <div className="col-span-2">
            <RegularSelectInput
              options={[
                {
                  label: "--- Select ---",
                  value: "",
                },
                {
                  label: "Bank Transfer",
                  value: "Bank Transfer",
                },
                {
                  label: "Cheque",
                  value: "Cheque",
                },
              ]}
              id="payment_type"
              label="Payment Type"
              placeholder="e.g. Select Payment"
              required={true}
              {...form}
            />
          </div>
        </div>
      </div>
      <div className="pt-3">
        <span className="text-xs font-light">Payment From</span>
        <div className="grid grid-cols-2 gap-2">
          <div
            className={clsx({
              "col-span-2":
                form.values.payment_type === "Bank Transfer" ||
                !form.values.payment_type,
            })}
          >
            <TextInput
              type="text"
              id="payment_from.bank_name"
              label="Bank Name"
              placeholder="e.g. Access Bank"
              required={true}
              {...form}
            />
          </div>
          {form.values.payment_type === "Cheque" && (
            <div>
              <TextInput
                type="text"
                id="payment_from.cheque_number"
                label="Cheque Number"
                placeholder="e.g. Ch-0000000"
                required={true}
                {...form}
              />
            </div>
          )}
          {form.values.payment_type === "Cheque" && (
            <div className={clsx("col-span-2")}>
              <TextInput
                type="date"
                id="payment_from.date_on_cheque"
                label="Date on Cheque"
                placeholder="e.g. Access Bank"
                required={true}
                {...form}
              />
            </div>
          )}
        </div>
      </div>
      <div className="pt-3">
        <span className="text-xs font-light">Payment To</span>
        <div className="grid grid-cols-2 gap-2">
          <div className={clsx("col-span-2")}>
            <TextInput
              type="text"
              id="payment_to"
              label="Bank Name"
              placeholder="e.g. Access Bank"
              required={true}
              {...form}
            />
          </div>
        </div>
      </div>
      <div className="pt-3">
        <span className="text-xs font-light">Payment Details</span>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <TextInput
              type="number"
              postText={`Out of ${numeral(
                form.values.payment_details.exptectedAmount
              ).format("#,#.####")}`}
              step={0.01}
              id="payment_details.payment_amount"
              label="Amount"
              placeholder="e.g. 1200"
              required={true}
              {...form}
            />
          </div>
          <div>
            <CurrencyPicker
              id="payment_details.currency"
              label="Currency"
              placeholder="e.g. USD"
              disabled
              required={true}
              {...form}
            />
          </div>
          {/* <div>
            <TextInput
              type="date"
              id="date_of_receipt"
              label="Date of receipt"
              placeholder="e.g. 2022-01-01"
              required={true}
              {...form}
            />
          </div> */}
          <div className="col-span-2">
            <TextArea
              id="offer_payment_comment"
              label="Comment"
              placeholder="e.g. Comment goes here ..."
              required={true}
              {...form}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;
